import { useVirtualizer } from '@tanstack/react-virtual';
import { Col, Flex, Row, Skeleton, Spin, UploadProps } from 'antd';
import { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { type FileCard as FileCardType } from 'services';
import { useInfiniteFilesCard } from '../../../hooks/useInfiniteFilesCard';
import { useInfiniteFilesVariables, useSearchQuery } from '../../../store/filepicker.store';
import styles from '../FilePicker.module.scss';
import { EmptyFolder } from './EmptyFolder';
import { FileCard } from './FileCardV2';
import { FilesBreadcrumb } from './FilesBreadcrumb';
import { Filters } from './Filters';
import { FoldersList } from './FoldersList';

type BrowseFilesListProps = {
  toggleItem: (item: FileCardType) => void;
  getIsItemSelected: (itemId: string) => boolean;
  accepted?: 'image' | 'all';
  uploadButtonProps: UploadProps;
};

export const BrowseFilesList: FC<BrowseFilesListProps> = ({
  toggleItem,
  getIsItemSelected,
  accepted = 'all',
  uploadButtonProps,
}) => {
  const type = accepted === 'image' ? 'image' : undefined;

  const variables = useInfiniteFilesVariables({ type });

  const searchQuery = useSearchQuery();

  const { files, hasNextPage, fetchNextPage, isFetchingNextPage, isLoading, isFetching } =
    useInfiniteFilesCard(variables);

  const numberOfRows = useMemo(() => Math.ceil(files.length / 4), [files.length]);

  const rowVirtualizer = useVirtualizer({
    count: numberOfRows,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 140,
    overscan: 2,
    paddingEnd: hasNextPage ? 50 : 0,
  });

  const parentRef = useRef<HTMLDivElement>(null);

  const getFileIndexByRowIndex = useCallback(
    (rowIndex: number): number[] => {
      const fileIndexes: number[] = [];
      for (let i = 0; i < 4; i++) {
        const calculatedIndex = rowIndex * 4 + i;
        if (calculatedIndex >= files.length) break;
        fileIndexes.push(calculatedIndex);
      }
      return fileIndexes;
    },
    [files],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    const [lastItem] = [...rowVirtualizer.getVirtualItems()].reverse();

    if (!lastItem) {
      return;
    }

    if (lastItem.index >= numberOfRows - 1 && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [
    hasNextPage,
    fetchNextPage,
    numberOfRows,
    isFetchingNextPage,
    rowVirtualizer.getVirtualItems,
    rowVirtualizer.getVirtualItems(),
  ]);

  return (
    <>
      <FilesBreadcrumb />
      <FoldersList />
      {isLoading ? (
        <>
          <Flex gap={16} style={{ width: '100%', overflowX: 'hidden' }}>
            <Skeleton.Node fullSize active style={{ height: 120, minWidth: 120 }} />
            <Skeleton.Node fullSize active style={{ height: 120, minWidth: 120 }} />
            <Skeleton.Node fullSize active style={{ height: 120, minWidth: 120 }} />
            <Skeleton.Node fullSize active style={{ height: 120, minWidth: 120 }} />
          </Flex>
        </>
      ) : (
        <>
          {!files.length ? (
            <EmptyFolder searchQuery={searchQuery} uploadButtonProps={uploadButtonProps} />
          ) : (
            <div>
              <Filters isFetching={isFetching} />
              <div
                ref={parentRef}
                style={{
                  height: `400px`,
                  width: `100%`,
                  overflow: 'auto',
                }}
              >
                <div
                  style={{
                    height: `${rowVirtualizer.getTotalSize()}px`,
                    width: '100%',
                    position: 'relative',
                  }}
                >
                  {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                    const filesIndexForThisRow = getFileIndexByRowIndex(virtualRow.index);
                    return (
                      <Row
                        gutter={[8, 8]}
                        key={virtualRow.index}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: `${virtualRow.size}px`,
                          transform: `translateY(${virtualRow.start}px)`,
                        }}
                      >
                        {filesIndexForThisRow.map((fileIndex) => (
                          <Col key={fileIndex} span={6}>
                            <FileCard
                              file={files[fileIndex]}
                              isChecked={getIsItemSelected(files[fileIndex].id)}
                              onClick={() => toggleItem(files[fileIndex])}
                            />
                          </Col>
                        ))}
                      </Row>
                    );
                  })}
                  {isFetchingNextPage && (
                    <div className={styles.loader} style={{ position: 'absolute', bottom: 0, width: '100%' }}>
                      <Spin size="large" />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};
